import type { PlayerColor } from '@/shared/board/types'
import type { Move, TokenPosition } from '@/shared/game/types'
import { TOKENS_PER_PLAYER } from './board'
import { base, samePosition } from './track'

/**
 * Расстановка фишек — порт App\Domain\Ludo\GameState.
 *
 * Неизменяемая: ход не меняет расстановку, а возвращает новую. На этом
 * держится правило максимума — «а что будет, если сходить так» проверяется
 * без порчи текущей позиции.
 */
export type GameState = {
  readonly players: readonly PlayerColor[]
  readonly tokens: Readonly<Record<string, readonly TokenPosition[]>>
  /**
   * Фишки под щитом (режим с бонусами): их нельзя срубить. Ключ —
   * «цвет:номер». Необязательно: в классике щитов нет, и расстановка из
   * хранилища и корпуса партий их не несёт.
   */
  readonly shielded?: readonly string[]
}

/** Ссылка на конкретную фишку: чья она и какая по счёту. */
export type TokenRef = { color: PlayerColor; index: number }

/** Начало партии: все фишки во дворах. */
export function startState(players: PlayerColor[]): GameState {
  const tokens: Record<string, TokenPosition[]> = {}

  for (const player of players) {
    tokens[player] = Array.from({ length: TOKENS_PER_PLAYER }, () => base())
  }

  return { players, tokens }
}

/** Расстановка из хранилища или из ответа сервера. */
export function restoreState(
  players: PlayerColor[],
  tokens: Record<string, TokenPosition[]>,
): GameState {
  return { players, tokens }
}

export function tokensOf(state: GameState, color: PlayerColor): readonly TokenPosition[] {
  return state.tokens[color] ?? []
}

/** Кто стоит на клетке кольца. Кольцо общее, поэтому ищем среди всех. */
export function ringOccupant(state: GameState, index: number): TokenRef | null {
  for (const player of state.players) {
    const at = tokensOf(state, player).findIndex(
      (position) => position.location === 'ring' && position.index === index,
    )

    if (at !== -1) return { color: player, index: at }
  }

  return null
}

/** Кто стоит на клетке дома. Дом свой у каждого, чужих там не бывает. */
export function homeOccupant(state: GameState, color: PlayerColor, index: number): TokenRef | null {
  const at = tokensOf(state, color).findIndex(
    (position) => position.location === 'home' && position.index === index,
  )

  return at === -1 ? null : { color, index: at }
}

/** Кто стоит на позиции — с точки зрения фишки этого цвета. */
export function occupant(
  state: GameState,
  color: PlayerColor,
  position: TokenPosition,
): TokenRef | null {
  if (position.location === 'ring') return ringOccupant(state, position.index)
  if (position.location === 'home') return homeOccupant(state, color, position.index)

  return null
}

/** Новое состояние после хода. Срубленная фишка уходит во двор. */
export function applyMove(state: GameState, move: Move): GameState {
  const tokens: Record<string, TokenPosition[]> = {}

  for (const player of state.players) {
    tokens[player] = [...tokensOf(state, player)]
  }

  tokens[move.color][move.token] = move.to

  if (move.captures !== null) {
    tokens[move.captures.color][move.captures.token] = base()
  }

  // Щит едет вместе с фишкой: он её, а не клетки.
  return { players: state.players, tokens, shielded: state.shielded }
}

/** То же расположение, но с этими фишками под щитом — вместо прежних. */
export function withShields(state: GameState, refs: readonly TokenRef[]): GameState {
  return { ...state, shielded: refs.map(shieldKey) }
}

/** Под щитом ли фишка: срубить её нельзя. */
export function isShielded(state: GameState, ref: TokenRef): boolean {
  return state.shielded?.includes(shieldKey(ref)) ?? false
}

function shieldKey(ref: TokenRef): string {
  return `${ref.color}:${ref.index}`
}

/**
 * Победа: домашняя дорожка занята целиком.
 *
 * По числу фишек в доме, а не по клеткам: клеток столько же, сколько фишек,
 * и на клетке стоит не больше одной.
 */
export function hasWon(state: GameState, color: PlayerColor): boolean {
  return (
    tokensOf(state, color).filter((position) => position.location === 'home').length ===
    TOKENS_PER_PLAYER
  )
}

/** Позиция конкретной фишки. */
export function positionOf(state: GameState, ref: TokenRef): TokenPosition {
  return tokensOf(state, ref.color)[ref.index] ?? base()
}

/** Стоит ли фишка ровно здесь — для сравнений в правилах. */
export function standsAt(state: GameState, ref: TokenRef, position: TokenPosition): boolean {
  return samePosition(positionOf(state, ref), position)
}
